// src/components/tutorials/JS_Array_Sort_Advanced.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };
  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

// 🔹 Random border color generator
function getRandomColor() {
  const colors = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#d97706",
    "#9333ea",
    "#0891b2",
    "#f59e0b",
    "#e11d48",
    "#0ea5e9",
    "#84cc16",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// 🔹 Output Box Component
function OutputBox({ children }) {
  const borderColor = getRandomColor();
  return (
    <div
      className="output-box"
      style={{
        border: `2px dashed ${borderColor}`,
        transition: "border-color 0.3s ease",
        padding: "0.5rem 1rem",
        borderRadius: "10px",
        background: "#f9fafb",
        color: "#000",
        marginTop: "0.5rem",
      }}
    >
      {children}
    </div>
  );
}

export default function JS_Array_Sort_Advanced() {
  const refs = {
    alphaSort: useRef(null),
    sortReverse: useRef(null),
    toSorted: useRef(null),
    toReversed: useRef(null),
    sortObjects: useRef(null),
    numericSort: useRef(null),
    randomSort: useRef(null),
    mathMinMax: useRef(null),
    homemadeMinMax: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const keyPoints = [
    { key: "alphaSort", label: "Alphabetic Sort" },
    { key: "sortReverse", label: "Array sort() & reverse()" },
    { key: "toSorted", label: "Array toSorted()" },
    { key: "toReversed", label: "Array toReversed()" },
    { key: "sortObjects", label: "Sorting Objects" },
    { key: "numericSort", label: "Numeric Sort" },
    { key: "randomSort", label: "Random Sort" },
    { key: "mathMinMax", label: "Math.min() & Math.max()" },
    { key: "homemadeMinMax", label: "Home Made Min/Max" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript Array Sorting (Advanced)</h1>

      <p className="tutorial-intro">
        JavaScript provides multiple ways to sort arrays — alphabetically,
        numerically, and even immutably (with new ES2023 methods). Let’s explore
        them all with interactive examples.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h1 style={{ color: "#075985", fontSize: "2.4rem", margin: 0 }}>
          Array Sorting & Math Utilities
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click below to jump to any topic.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1.25rem",
          }}
        >
          {keyPoints.map((l) => (
            <p key={l.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(l.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {l.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Alphabetic Sort */}
      <div ref={refs.alphaSort} className="tutorial-section">
        <h2 className="section-title">1️⃣ Alphabetic Sort</h2>
        <p>
          The <code className="highlight_code">sort()</code> method sorts
          strings alphabetically by default.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
fruits.sort();
console.log(fruits);`}
          />
          <TryItButton
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
fruits.sort();
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["Apple", "Banana", "Mango", "Orange"]</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Array sort() & reverse() */}
      <div ref={refs.sortReverse} className="tutorial-section">
        <h2 className="section-title">2️⃣ Array sort() & reverse()</h2>
        <p>
          Use <code className="highlight_code">reverse()</code> to invert the
          sorted array.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
fruits.sort();
fruits.reverse();
console.log(fruits);`}
          />
          <TryItButton
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
fruits.sort();
fruits.reverse();
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["Orange", "Mango", "Banana", "Apple"]</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Array toSorted() */}
      <div ref={refs.toSorted} className="tutorial-section">
        <h2 className="section-title">3️⃣ Array toSorted()</h2>
        <p>
          ES2023 introduced <code className="highlight_code">toSorted()</code> —
          it returns a <b>new sorted array</b> without modifying the original.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
const sortedFruits = fruits.toSorted();
console.log(sortedFruits);
console.log(fruits); // original unchanged`}
          />
          <TryItButton
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
const sortedFruits = fruits.toSorted();
console.log(sortedFruits);
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>
            ["Apple", "Banana", "Mango", "Orange"]
            {"\n"}
            ["Banana", "Orange", "Apple", "Mango"]
          </pre>
        </OutputBox>
      </div>

      {/* 4️⃣ Array toReversed() */}
      <div ref={refs.toReversed} className="tutorial-section">
        <h2 className="section-title">4️⃣ Array toReversed()</h2>
        <p>
          Similar to <code className="highlight_code">toSorted()</code>,{" "}
          <code className="highlight_code">toReversed()</code> returns a new
          reversed array.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
const reversed = fruits.toReversed();
console.log(reversed);
console.log(fruits);`}
          />
          <TryItButton
            code={`const fruits = ["Banana", "Orange", "Apple", "Mango"];
const reversed = fruits.toReversed();
console.log(reversed);
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>
            ["Mango", "Apple", "Orange", "Banana"]
            {"\n"}
            ["Banana", "Orange", "Apple", "Mango"]
          </pre>
        </OutputBox>
      </div>

      {/* 5️⃣ Sorting Objects */}
      <div ref={refs.sortObjects} className="tutorial-section">
        <h2 className="section-title">5️⃣ Sorting Objects</h2>
        <p>Use a compare function to sort by object property.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const students = [
  { name: "Alice", score: 85 },
  { name: "Bob", score: 92 },
  { name: "Eve", score: 78 },
];
students.sort((a, b) => b.score - a.score);
console.log(students);`}
          />
          <TryItButton
            code={`const students = [
  { name: "Alice", score: 85 },
  { name: "Bob", score: 92 },
  { name: "Eve", score: 78 },
];
students.sort((a, b) => b.score - a.score);
console.log(students);`}
          />
        </div>
        <OutputBox>
          <pre>
            [
            {`{ name: "Bob", score: 92 }, { name: "Alice", score: 85 }, { name: "Eve", score: 78 }`}
            ]
          </pre>
        </OutputBox>
      </div>

      {/* 6️⃣ Numeric Sort */}
      <div ref={refs.numericSort} className="tutorial-section">
        <h2 className="section-title">6️⃣ Numeric Sort</h2>
        <p>
          By default, <code className="highlight_code">sort()</code> converts
          numbers to strings. Use a compare function to sort correctly.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const numbers = [40, 100, 1, 5, 25, 10];
numbers.sort();
console.log(numbers); // Wrong: [1, 10, 100, 25, 40, 5]
numbers.sort((a, b) => a - b);
console.log(numbers); // Correct: [1, 5, 10, 25, 40, 100]`}
          />
          <TryItButton
            code={`const numbers = [40, 100, 1, 5, 25, 10];
numbers.sort();
console.log(numbers);
numbers.sort((a, b) => a - b);
console.log(numbers);`}
          />
        </div>
        <OutputBox>
          <pre>[1, 10, 100, 25, 40, 5] [1, 5, 10, 25, 40, 100]</pre>
        </OutputBox>
      </div>

      {/* 7️⃣ Random Sort */}
      <div ref={refs.randomSort} className="tutorial-section">
        <h2 className="section-title">7️⃣ Random Sort</h2>
        <p>
          Shuffle an array randomly using{" "}
          <code className="highlight_code">sort()</code> and{" "}
          <code className="highlight_code">Math.random()</code>.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const points = [40, 100, 1, 5, 25, 10];
points.sort(() => 0.5 - Math.random());
console.log(points);`}
          />
          <TryItButton
            code={`const points = [40, 100, 1, 5, 25, 10];
points.sort(() => 0.5 - Math.random());
console.log(points);`}
          />
        </div>
        <OutputBox>
          <pre>[Randomly shuffled array]</pre>
        </OutputBox>
      </div>

      {/* 8️⃣ Math.min() & Math.max() */}
      <div ref={refs.mathMinMax} className="tutorial-section">
        <h2 className="section-title">8️⃣ Math.min() & Math.max()</h2>
        <p>
          Use <code className="highlight_code">Math.min()</code> and{" "}
          <code className="highlight_code">Math.max()</code> to find smallest
          and largest values.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const numbers = [40, 100, 1, 5, 25, 10];
console.log(Math.max(...numbers)); // 100
console.log(Math.min(...numbers)); // 1`}
          />
          <TryItButton
            code={`const numbers = [40, 100, 1, 5, 25, 10];
console.log(Math.max(...numbers));
console.log(Math.min(...numbers));`}
          />
        </div>
        <OutputBox>
          <pre>100 1</pre>
        </OutputBox>
      </div>

      {/* 9️⃣ Home Made Min/Max */}
      <div ref={refs.homemadeMinMax} className="tutorial-section">
        <h2 className="section-title">9️⃣ Home Made Min/Max</h2>
        <p>You can also loop manually to find min or max value in an array.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const numbers = [40, 100, 1, 5, 25, 10];

function myArrayMin(arr) {
  let min = arr[0];
  for (let i of arr) if (i < min) min = i;
  return min;
}

function myArrayMax(arr) {
  let max = arr[0];
  for (let i of arr) if (i > max) max = i;
  return max;
}

console.log(myArrayMin(numbers)); // 1
console.log(myArrayMax(numbers)); // 100`}
          />
          <TryItButton
            code={`const numbers = [40, 100, 1, 5, 25, 10];
function myArrayMin(arr){ let min = arr[0]; for(let i of arr) if(i<min) min=i; return min;}
function myArrayMax(arr){ let max = arr[0]; for(let i of arr) if(i>max) max=i; return max;}
console.log(myArrayMin(numbers));
console.log(myArrayMax(numbers));`}
          />
        </div>
        <OutputBox>
          <pre>1 100</pre>
        </OutputBox>
      </div>

      {/* Footer */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Learn stable sorting in modern browsers.</li>
          <li>Use Intl.Collator for locale-aware string sorting.</li>
          <li>Try immutable array methods (`toSorted`, `toReversed`).</li>
        </ul>
      </div>
    </div>
  );
}
